import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState } from "../ui/State";
export function ReserveForecastAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building") ?? "",
    scope = "?building=" + building,
    [model, setModel] = useState<any>(),
    [count, setCount] = useState(1),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<object | null>(null);
  const load = () =>
    api<any>("/api/alpha/reserve-forecasts" + scope).then(setModel);
  useEffect(() => {
    setModel(undefined);
    if (building) void load().catch(setError);
  }, [building]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      input: {
        startYear: Number(f.get("year")),
        years: Number(f.get("years")),
        openingReserve: f.get("opening"),
        annualContribution: f.get("contribution"),
        inflationBps: Number(f.get("inflation")) * 100,
        assumptions: f.get("assumptions"),
        assets: Array.from({ length: count }, (_, i) => ({
          assetId: f.get("asset" + i),
          condition: f.get("condition" + i),
          basis: f.get("basis" + i),
          replacementYear: Number(f.get("replacement" + i)),
          cost: f.get("cost" + i),
        })),
      },
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      await alphaSave(pending, "/api/alpha/reserve-forecasts" + scope, {
        method: "POST",
        body: JSON.stringify(pending.current),
      });
      pending.current = null;
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Asset replacement and reserve forecast</h1>
      <p>
        Compare funding scenarios using reviewed asset condition and replacement
        estimates. This does not issue charges or authorise works.
      </p>
      {!building ? (
        <Link to="/alpha">Choose a building</Link>
      ) : (
        <>
          {Boolean(error) && <ErrorState error={error} />}
          <form onSubmit={save}>
            <fieldset disabled={busy || !!pending.current}>
              <legend>Scenario assumptions</legend>
              <label>
                Start year
                <input
                  name="year"
                  type="number"
                  min={2020}
                  max={2100}
                  defaultValue={2027}
                  required
                />
              </label>
              <label>
                Years
                <input
                  name="years"
                  type="number"
                  min={1}
                  max={30}
                  defaultValue={10}
                  required
                />
              </label>
              <label>
                Reviewed opening reserve (£)
                <input
                  name="opening"
                  inputMode="decimal"
                  required
                  defaultValue="0.00"
                />
              </label>
              <label>
                Annual contribution (£)
                <input
                  name="contribution"
                  inputMode="decimal"
                  required
                  defaultValue="0.00"
                />
              </label>
              <label>
                Annual cost inflation (%)
                <input
                  name="inflation"
                  type="number"
                  min={0}
                  max={20}
                  step="0.01"
                  defaultValue={0}
                  required
                />
              </label>
              <label>
                Balance source, price basis and assumptions
                <textarea
                  name="assumptions"
                  minLength={10}
                  maxLength={4000}
                  required
                />
              </label>
              {Array.from({ length: count }, (_, i) => (
                <section key={i}>
                  <h2>Replacement estimate {i + 1}</h2>
                  <label>
                    Asset
                    <select name={"asset" + i} required>
                      <option value="">Choose asset</option>
                      {model?.assets.map((a: any) => (
                        <option key={a.id} value={a.id}>
                          {a.display_name} · {a.asset_code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Reviewed condition
                    <textarea
                      name={"condition" + i}
                      minLength={3}
                      maxLength={1000}
                      required
                    />
                  </label>
                  <label>
                    Estimate source and VAT basis
                    <textarea
                      name={"basis" + i}
                      minLength={10}
                      maxLength={2000}
                      required
                    />
                  </label>
                  <label>
                    Replacement year
                    <input name={"replacement" + i} type="number" required />
                  </label>
                  <label>
                    Cost at start-year prices (£)
                    <input name={"cost" + i} inputMode="decimal" required />
                  </label>
                </section>
              ))}
              {count < 100 && (
                <button type="button" className="secondary" onClick={() => setCount(count + 1)}>
                  Add asset estimate
                </button>
              )}
            </fieldset>
            <button disabled={busy}>
              {pending.current ? "Retry same scenario" : "Prepare forecast"}
            </button>
          </form>
          <h2>Retained scenarios</h2>
          {model?.forecasts.length===0&&<p className="empty">No reserve forecasts have been prepared for this building.</p>}
          {model?.forecasts.map((f: any) => (
            <section className="panel" key={f.id}>
              <h3>
                {f.snapshot.input.startYear} ·{" "}
                {f.status === "Approved" ? "Reviewed" : f.status}
              </h3>
              <p>{f.commentary}</p>
              <p>{f.snapshot.basis}</p>
              <p>
                Minimum equal annual contribution to avoid a year-end shortfall:
                £{f.snapshot.minimumAnnualContribution}
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Opening (£)</th>
                    <th>Contribution (£)</th>
                    <th>Expenditure (£)</th>
                    <th>Closing (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {f.snapshot.years.map((y: any) => (
                    <tr key={y.year}>
                      <td>{y.year}</td>
                      <td>{y.opening}</td>
                      <td>{y.contribution}</td>
                      <td>{y.expenditure}</td>
                      <td>
                        {y.closing}
                        {y.shortfall !== "0.00" ? " — shortfall" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {f.snapshot.estimates.map((a: any) => (
                <p key={a.assetId}>
                  {
                    f.snapshot.source.find((s: any) => s.id === a.assetId)
                      ?.display_name
                  }
                  : {a.condition}. {a.basis} · {a.replacementYear}: £
                  {a.inflatedCost}
                </p>
              ))}
              {f.status === "Draft" && (
                <Review
                  id={f.id}
                  version={f.version}
                  scope={scope}
                  done={load}
                />
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
function Review({
  id,
  version,
  scope,
  done,
}: {
  id: string;
  version: number;
  scope: string;
  done: () => Promise<void>;
}) {
  const pending = useRef<object | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  return (
    <>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        An independent reviewer must confirm the retained assumptions and
        condition estimates.
      </p>
      <button
        disabled={busy}
        onClick={() => {
          pending.current ??= {
            expectedVersion: version,
            idempotencyKey: crypto.randomUUID(),
          };
          setBusy(true);
          void alphaSave(
            pending,
            "/api/alpha/reserve-forecasts/" + id + "/review" + scope,
            { method: "POST", body: JSON.stringify(pending.current) },
          )
            .then(async () => {
              pending.current = null;
              await done();
            })
            .catch(setError)
            .finally(() => setBusy(false));
        }}
      >
        {pending.current ? "Retry same review" : "Mark forecast reviewed"}
      </button>
    </>
  );
}
